import { Account } from "@workspace/api-client-react";
import { useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { RowSelectionState, OnChangeFn } from "@tanstack/react-table";
import {
  AccountsTable,
  accountHealthState,
  type HealthState,
} from "@/components/accounts-table";

export type PhoneMode = "automated" | "manual";

const UNASSIGNED = "Unassigned";

interface AccountGroup {
  key: string;
  pullerName: string;
  accounts: Account[];
  ids: string[];
  health: Record<HealthState, number>;
  automated: number;
  manual: number;
}

function buildGroups(
  accounts: Account[],
  modeFor: (account: Account) => PhoneMode,
): AccountGroup[] {
  const map = new Map<string, AccountGroup>();
  for (const account of accounts) {
    const name = account.pullerName?.trim() || UNASSIGNED;
    let group = map.get(name);
    if (!group) {
      group = {
        key: name,
        pullerName: name,
        accounts: [],
        ids: [],
        health: { good: 0, warning: 0, bad: 0 },
        automated: 0,
        manual: 0,
      };
      map.set(name, group);
    }
    group.accounts.push(account);
    group.ids.push(String(account.id));
    group.health[accountHealthState(account)]++;
    if (modeFor(account) === "automated") group.automated++;
    else group.manual++;
  }

  // Pullers alphabetically, with the Unassigned bucket pinned to the bottom.
  return [...map.values()].sort((a, b) => {
    if (a.pullerName === UNASSIGNED) return 1;
    if (b.pullerName === UNASSIGNED) return -1;
    return a.pullerName.localeCompare(b.pullerName);
  });
}

const HEALTH_CHIP: Record<HealthState, string> = {
  good: "bg-green-500/10 text-green-500 border-green-500/30",
  warning: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  bad: "bg-red-500/10 text-red-500 border-red-500/30",
};

function HealthChip({ state, count }: { state: HealthState; count: number }) {
  const label = state === "good" ? "Good" : state === "warning" ? "Warning" : "Bad";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] ${HEALTH_CHIP[state]}`}
    >
      <span className="font-medium">{count}</span>
      <span className="opacity-80">{label}</span>
    </span>
  );
}

interface AccountGroupSectionProps {
  group: AccountGroup;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  collapsed: boolean;
  onToggleCollapsed: (key: string) => void;
}

function AccountGroupSection({
  group,
  rowSelection,
  onRowSelectionChange,
  collapsed,
  onToggleCollapsed,
}: AccountGroupSectionProps) {
  const idSet = useMemo(() => new Set(group.ids), [group.ids]);

  // Scope the shared selection down to this bucket so each AccountsTable's
  // "select all" header only toggles its own rows, while changes merge back
  // into the page-wide selection and leave other buckets untouched.
  const scoped = useMemo(() => {
    const out: RowSelectionState = {};
    for (const id of group.ids) if (rowSelection[id]) out[id] = true;
    return out;
  }, [group.ids, rowSelection]);

  const handleChange: OnChangeFn<RowSelectionState> = (updater) => {
    const next = typeof updater === "function" ? updater(scoped) : updater;
    onRowSelectionChange((prev) => {
      const merged: RowSelectionState = {};
      for (const [id, val] of Object.entries(prev)) {
        if (!idSet.has(id) && val) merged[id] = true;
      }
      for (const [id, val] of Object.entries(next)) {
        if (val) merged[id] = true;
      }
      return merged;
    });
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggleCollapsed(group.key)}
        aria-expanded={!collapsed}
        className="sticky top-0 z-20 flex w-full flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-panel px-3 py-2 text-left transition-colors hover:bg-muted/40"
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground">
          {group.pullerName}
        </span>
        <span className="font-mono text-xs text-muted-foreground">
          {group.accounts.length} account{group.accounts.length === 1 ? "" : "s"}
        </span>
        <span className="text-border">·</span>
        <div className="flex items-center gap-1.5">
          <HealthChip state="good" count={group.health.good} />
          <HealthChip state="warning" count={group.health.warning} />
          <HealthChip state="bad" count={group.health.bad} />
        </div>
        <span className="text-border">·</span>
        <span className="font-mono text-[11px] text-muted-foreground">
          {group.automated} automated, {group.manual} manual
        </span>
      </button>
      {!collapsed && (
        <AccountsTable
          data={group.accounts}
          rowSelection={scoped}
          onRowSelectionChange={handleChange}
        />
      )}
    </div>
  );
}

interface AccountsGroupedProps {
  data: Account[];
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  modeFor: (account: Account) => PhoneMode;
  collapsed: Set<string>;
  onToggleCollapsed: (key: string) => void;
}

export function AccountsGrouped({
  data,
  rowSelection,
  onRowSelectionChange,
  modeFor,
  collapsed,
  onToggleCollapsed,
}: AccountsGroupedProps) {
  const groups = useMemo(
    () => buildGroups(data, modeFor),
    [data, modeFor],
  );

  if (groups.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-8">
        No accounts found.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <AccountGroupSection
          key={group.key}
          group={group}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          collapsed={collapsed.has(group.key)}
          onToggleCollapsed={onToggleCollapsed}
        />
      ))}
    </div>
  );
}
